import { RhinoEventBus, RhinoEventContext, rhinoEvents } from '../events';
import { browserLocaleContext, browserVisitContext } from './browserContext';
import { sanitizeProperties, Scalar } from './sanitize';
import { analyticsId } from './id';
export { analyticsId } from './id';

// Server-rendered by rhino_analytics_tag; absent when analytics is off (and
// in test setups, which construct window.rhino with env alone). Read through
// a local cast rather than the window.rhino global declared in config/env so
// the analytics subpath depends on nothing else in the package.
export interface RhinoAnalyticsBootstrap {
  enabled: boolean;
  endpoint: string;
}

export interface AnalyticsClientOptions {
  enabled?: boolean;
  endpoint?: string;
  bus?: RhinoEventBus;
  batchSize?: number;
  flushAfterMs?: number;
  engagedAfterMs?: number;
  storageKey?: string;
}

export interface PageProps {
  route?: string;
  path?: string;
}

interface QueuedEvent {
  uuid: string;
  name: string;
  time: string;
  page_view_id?: string;
  model?: string;
  base_owner_id?: string;
  properties: Record<string, Scalar>;
}

interface Engagement {
  page: { path: string; route?: string };
  visibleMs: number;
  since: number | undefined;
  timer: ReturnType<typeof setTimeout> | undefined;
}

const STORAGE_CAP = 200;

const bootstrap = (): Partial<RhinoAnalyticsBootstrap> =>
  typeof window === 'undefined'
    ? {}
    : ((window as { rhino?: { analytics?: RhinoAnalyticsBootstrap } }).rhino
        ?.analytics ?? {});

// Batches bus events to the collector. Holds no tokens: the server set them
// as HttpOnly cookies and reads them back off the request. The only client
// state the server needs is the page view id, stamped on every event and
// sent as a header on API calls by the networking layer.
export class RhinoAnalyticsClient {
  readonly enabled: boolean;
  readonly endpoint: string;
  private pageViewId: string | undefined;
  private pageContext: PageProps | undefined;
  private pageContextResolver: (() => PageProps | undefined) | undefined;

  get currentPageViewId() {
    this.syncPageContext();
    return this.pageViewId;
  }

  // Resolve committed route matches at the point an event needs context.
  // Component effects can precede the router's onResolved notification.
  setPageContextResolver(resolver: () => PageProps | undefined) {
    this.pageContextResolver = resolver;
    return () => {
      if (this.pageContextResolver === resolver)
        this.pageContextResolver = undefined;
    };
  }

  syncPageContext() {
    if (!this.enabled) return;
    const page = this.pageContextResolver?.();
    if (
      page &&
      (!this.pageViewId ||
        page.path !== this.pageContext?.path ||
        page.route !== this.pageContext?.route)
    ) {
      this.page(page);
    }
  }
  private baseOwnerId: string | undefined;
  private queue: QueuedEvent[] = [];
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private engagement: Engagement | undefined;
  private readonly batchSize: number;
  private readonly flushAfterMs: number;
  private readonly engagedAfterMs: number;
  private readonly storageKey: string;
  private readonly bus: RhinoEventBus;
  private readonly unsubscribe: () => void;

  constructor(options: AnalyticsClientOptions = {}) {
    const boot = bootstrap();
    this.enabled = options.enabled ?? boot.enabled ?? false;
    this.endpoint =
      options.endpoint ?? boot.endpoint ?? '/api/_rhino/analytics/events';
    this.bus = options.bus ?? rhinoEvents;
    this.batchSize = Math.max(1, Math.min(10, options.batchSize ?? 10));
    this.flushAfterMs = options.flushAfterMs ?? 2000;
    this.engagedAfterMs = options.engagedAfterMs ?? 10_000;
    this.storageKey = options.storageKey ?? 'rhino.analytics.queue';
    this.unsubscribe = this.bus.subscribe((event) =>
      this.enqueue(event.name, event.payload, event.context)
    );
    if (this.enabled && typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.onPageHide);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.drainStorage();
    }
  }

  // A screen render: a fresh page view id that every later event carries,
  // and an engagement timer that fires once after ten visible seconds.
  page(props: PageProps = {}) {
    if (!this.enabled) return;
    this.pageViewId = analyticsId();
    const page = {
      path: props.path ?? window.location.pathname,
      route: props.route
    };
    this.pageContext = page;
    this.bus.notify('page.viewed', page);
    this.startEngagement(page);
  }

  track(name: string, props: Record<string, unknown> = {}) {
    this.bus.notify(name, props);
  }

  // Pin in-flight workflow events to the initiating page/organization. A
  // sign-out reset invalidates the emitter, so late saves cannot become events
  // for the next signed-in user. Page identity is captured on the first event.
  capture(baseOwnerId?: string | number | null) {
    const generation = this.generation;
    let context: RhinoEventContext | undefined;
    return (name: string, properties: Record<string, unknown> = {}) => {
      if (generation !== this.generation || !this.enabled) return;
      context ??= {
        pageViewId: this.currentPageViewId,
        baseOwnerId:
          baseOwnerId == null ? this.baseOwnerId : String(baseOwnerId)
      };
      this.bus.notify(name, properties, context);
    };
  }

  setBaseOwner(id?: string | number | null) {
    this.baseOwnerId = id === undefined || id === null ? undefined : String(id);
  }

  reset() {
    this.generation += 1;
    this.queue = [];
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch {
      /* unavailable */
    }
    this.pageViewId = undefined;
    this.pageContext = undefined;
    this.baseOwnerId = undefined;
    this.stopEngagement();
  }

  dispose() {
    this.unsubscribe();
    this.stopEngagement();
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.onPageHide);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  async flush(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.queue.length);
    const generation = this.generation;
    for (let offset = 0; offset < events.length; offset += this.batchSize) {
      const batch = events.slice(offset, offset + this.batchSize);
      if (generation !== this.generation) return;
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            events: batch,
            visit: this.visitAttributes()
          }),
          keepalive: true,
          credentials: 'same-origin'
        });
        // Keep transient failures for the next page load. Invalid events and
        // disabled collectors must not poison every subsequent batch.
        if (
          !response.ok &&
          (response.status === 429 || response.status >= 500)
        ) {
          if (generation === this.generation) this.store(batch);
        }
      } catch {
        if (generation === this.generation) this.store(batch);
      }
    }
  }

  // What only the browser knows about the visit; the server fills blank
  // columns from it, so repeating it on every batch is harmless.
  private visitAttributes() {
    if (typeof window === 'undefined') return {};
    return {
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
      ...browserVisitContext()
    };
  }

  private enqueue(
    name: string,
    payload: Record<string, unknown>,
    context?: RhinoEventContext
  ) {
    if (!this.enabled) return;
    if (!context) this.syncPageContext();
    this.queue.push({
      uuid: analyticsId(),
      name,
      time: new Date().toISOString(),
      page_view_id: context ? context.pageViewId : this.pageViewId,
      base_owner_id: context ? context.baseOwnerId : this.baseOwnerId,
      model: typeof payload.model === 'string' ? payload.model : undefined,
      properties: sanitizeProperties({ ...payload, ...browserLocaleContext() })
    });
    if (this.queue.length >= this.batchSize) {
      void this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => void this.flush(), this.flushAfterMs);
    }
  }

  private onPageHide = () => {
    this.pauseEngagement();
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.queue.length);
    for (let offset = 0; offset < events.length; offset += this.batchSize) {
      const batch = events.slice(offset, offset + this.batchSize);
      const body = JSON.stringify({
        events: batch,
        visit: this.visitAttributes()
      });
      const sent =
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(
          this.endpoint,
          new Blob([body], { type: 'application/json' })
        );
      if (!sent) this.store(batch);
    }
  };

  private onVisibility = () => {
    if (document.visibilityState === 'visible') this.resumeEngagement();
    else this.pauseEngagement();
  };

  // Engagement is ten seconds of visible time, once per page view: the
  // signal GA4 uses to separate an engaged session from a bounce. The event
  // names the page so it reads on its own, without a join to the page view.
  private startEngagement(page: Engagement['page']) {
    this.stopEngagement();
    this.engagement = {
      page,
      visibleMs: 0,
      since: undefined,
      timer: undefined
    };
    if (
      typeof document === 'undefined' ||
      document.visibilityState !== 'hidden'
    ) {
      this.resumeEngagement();
    }
  }

  private resumeEngagement() {
    const e = this.engagement;
    if (!e || e.since !== undefined) return;
    e.since = Date.now();
    e.timer = setTimeout(() => {
      if (this.engagement !== e) return;
      this.engagement = undefined;
      this.bus.notify('page.engaged', e.page);
    }, this.engagedAfterMs - e.visibleMs);
  }

  private pauseEngagement() {
    const e = this.engagement;
    if (!e || e.since === undefined) return;
    e.visibleMs += Date.now() - e.since;
    e.since = undefined;
    if (e.timer) clearTimeout(e.timer);
    e.timer = undefined;
  }

  private stopEngagement() {
    if (this.engagement?.timer) clearTimeout(this.engagement.timer);
    this.engagement = undefined;
  }

  private store(events: QueuedEvent[]) {
    try {
      const existing: QueuedEvent[] = JSON.parse(
        window.localStorage.getItem(this.storageKey) ?? '[]'
      );
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify([...existing, ...events].slice(-STORAGE_CAP))
      );
    } catch {
      // Storage unavailable (private mode, quota): the batch is lost, which
      // is the acceptable outcome for analytics.
    }
  }

  private drainStorage() {
    try {
      const stored = window.localStorage.getItem(this.storageKey);
      if (!stored) return;
      window.localStorage.removeItem(this.storageKey);
      this.queue.push(...(JSON.parse(stored) as QueuedEvent[]));
      void this.flush();
    } catch {
      // Same as store: nothing to do.
    }
  }
}

export const createAnalyticsClient = (options?: AnalyticsClientOptions) =>
  new RhinoAnalyticsClient(options);

export const defaultAnalyticsClient = new RhinoAnalyticsClient();
